import { Button, Card, Row, Space, Tabs, TabsProps, Tooltip, Tour } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ColumnComponent } from "../../components/ui";
import Table, { ColumnsType } from "antd/es/table";
import DataContext from "../../context/DataContext";
import { useParams } from "react-router-dom";
import {
  DownloadOutlined,
  EditOutlined,
  FolderAddOutlined,
  CopyOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { MultipleSelect, SearchInput } from "../../components/functional";
import { ContactProps } from "../../interfaces/interfaces";
import { useDemoModal } from "../../hooks";
import { exportContactList } from "../../utils/func/ExportFile";
import Link from "antd/es/typography/Link";
import { TablePaginationConfig } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import { CONTACT_INITIAL_STATE, TASK_INITIAL_STATE } from "../../utils/data";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

export const ContactListPage = () => {
  const { idClient } = useParams();
  const { DemoModal } = useDemoModal();
  const [activeTab, setActiveTab] = useState("1");
  const {
    getData,
    customerOptions,
    contactListPaginated,
    dataLoading,
    openItemModal,
  } = useContext(DataContext);
  const [selectedRowsToEdit] = useState<React.Key[]>([]);
  const ref1 = useRef(null);
  const [tableParams, setTableParams] = useState<TableParams>({});
  const [pageSize, setPageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [filters, setFilters] = useState<any>({
    active: true,
    search: undefined,
  });

  useEffect(() => {
    setFilters({ active: true, search: undefined });
  }, []);

  useEffect(() => {
    idClient && getData("customer", idClient);
  }, [idClient]);

  useEffect(() => {
    let { search, ...contactFilters } = filters;
    contactFilters = {
      ...contactFilters,
      page: currentPage,
      perPage: pageSize,
    };
    idClient &&
      idClient !== "" &&
      getData("contacts-paginated", idClient, search, contactFilters, false);
  }, [filters, currentPage, pageSize]);

  useEffect(() => {
    setTableParams({
      pagination: {
        pageSize: pageSize,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => `Total ${total} Contacts`,
        position: ["bottomCenter"],
        total: parseInt(contactListPaginated.pagination?.total) || 0,
        onChange: (page, pageSize) => {
          setCurrentPage(page);
          setTableParams((prev) => {
            return {
              ...prev,
              pagination: {
                ...prev.pagination,
                pageSize: pageSize,
              },
            };
          });
          setPageSize(pageSize);
        },
      },
    });
  }, [contactListPaginated.pagination]);

  const columns: ColumnsType<ContactProps> = [
    {
      title: "Actions",
      key: "edit",
      width: "10%",
      render: (params) => (
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Space direction="horizontal">
            <Tooltip title="Edit Contact" placement="left">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                onClick={() => {
                  openItemModal("contact", false, params);
                }}
                icon={<EditOutlined />}
                size="small"
              ></Button>
            </Tooltip>
            <Tooltip title="Duplicate Contact" placement="top">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                onClick={() => {
                  openItemModal("contact", true, params);
                }}
                icon={<CopyOutlined />}
                size="small"
              ></Button>
            </Tooltip>
            <Tooltip title="Add New Task" placement="right">
              <Button
                style={{ color: "gray", borderColor: "gray" }}
                icon={<ClockCircleOutlined />}
                onClick={() => {
                  openItemModal("task", true, {
                    ...TASK_INITIAL_STATE,
                    contact: params,
                    customer: params.customer || null,
                    project: null,
                    origin: "contact",
                  });
                }}
                size="small"
              ></Button>
            </Tooltip>
          </Space>
        </div>
      ),
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: "table-row",
      width: "10%",
      render: (_, { name }) => (
        <Tooltip title="Click to see Contact Info">
          <Link
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              maxWidth: 180,
            }}
          >
            {name}
          </Link>
        </Tooltip>
      ),
      onCell: (record) => {
        return {
          onClick: () => openItemModal("contact", false, record),
        };
      },
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 1,
      },
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      className: "table-row",
      render: (_, { customer }) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            maxWidth: 180,
          }}
        >
          {customer?.DisplayName}
        </div>
      ),
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 1,
      },
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: "Relationship",
      dataIndex: "relationship",
      key: "relationship",
      className: "table-row",
      width: "10%",
      render: (_, { relationship }) => <>{relationship}</>,
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
        multiple: 1,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phone",
      className: "table-row",
      width: "15%",
      render: (_, { phone }) => <>{phone}</>,
      sorter: {
        compare: (a, b) => a.phone.localeCompare(b.phone),
        multiple: 1,
      },
      responsive: ["xl", "lg", "md"],
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      className: "table-row",
      width: "15%",
      render: (_, { email }) => <div>{email}</div>,
      sorter: {
        compare: (a, b) => String(a.email).localeCompare(String(b.email)),
        multiple: 2,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      className: "table-row",
      width: "15%",
      render: (_, { address }) => <div>{address}</div>,
      sorter: {
        compare: (a, b) => String(a.address).localeCompare(String(b.address)),
        multiple: 2,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: "10%",
      className: "table-row",
      render: (_, { city }) => <div>{city}</div>,
      sorter: {
        compare: (a, b) => String(a.city).localeCompare(String(b.city)),
        multiple: 2,
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ["xl", "lg"],
    },
  ];

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Contacts",
      children: (
        <Table
          bordered
          loading={dataLoading}
          columns={columns}
          // rowSelection={rowSelection}
          dataSource={contactListPaginated.data}
          className="summary-client__table"
          pagination={tableParams.pagination}
          size="small"
          rowKey={"_id"}
          tableLayout="auto"
        />
      ),
    },
    {
      key: "2",
      label: "Leads",
      disabled: true,
    },
  ];

  const onChange = (key: string) => {
    setActiveTab(key);
  };

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <div ref={ref1}>
            <Card
              className="summary-client__card"
              styles={{ body: { display: "none" } }}
              actions={[
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Filter By Customer</label>
                  <Tooltip title="Filter By Customer">
                    <MultipleSelect
                      placeholder="Customer"
                      className="summary-client__card__actions__select"
                      options={customerOptions}
                      onSelect={(values) => {
                        let query = values.length > 0 ? values : null;
                        setFilters((prev: any) => ({
                          ...prev,
                          customers: query,
                        }));
                      }}
                    />
                  </Tooltip>
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Search</label>
                  <SearchInput
                    onSearch={(value: string) => {
                      if (value) {
                        setFilters((prev: any) => ({
                          ...prev,
                          search: value,
                        }));
                      } else {
                        setFilters((prev: any) => ({
                          ...prev,
                          search: undefined,
                        }));
                      }
                    }}
                  />
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Actions</label>
                  <Space direction="horizontal" size={0}>
                    <Tooltip title="Create new Contact" placement="bottom">
                      <Button
                        type="link"
                        icon={<FolderAddOutlined />}
                        size="large"
                        onClick={() =>
                          openItemModal("contact", true, {
                            ...CONTACT_INITIAL_STATE,
                            customer: null,
                          })
                        }
                      />
                    </Tooltip>

                    <Tooltip
                      title={
                        selectedRowsToEdit.length > 0
                          ? "Export Selected Data"
                          : "Export Data"
                      }
                      placement="bottom"
                    >
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={() => {
                          exportContactList(
                            contactListPaginated.data,
                            selectedRowsToEdit as string[],
                          );
                        }}
                      />
                    </Tooltip>
                  </Space>
                </Space>,
              ]}
            ></Card>
          </div>
        </ColumnComponent>
        <ColumnComponent>
          <Tabs
            style={{ padding: "0 10px" }}
            defaultActiveKey="1"
            items={items}
            onChange={onChange}
            activeKey={activeTab}
          />
        </ColumnComponent>
      </Row>

      <DemoModal />
    </>
  );
};
